import { useSyncExternalStore } from "react";
import { getPref, setPref, subscribePref } from "@/lib/storage/userPrefs";

/**
 * "Dismissed today, at this severity" — the persistence behind the escalating
 * expiry strips.
 *
 * Stored as ONE key per subject with the value `"<local-YYYY-MM-DD>:<ordinal>"`.
 * Two things that buys us over the previous hand-rolled version:
 *
 * - The rung lives in the value, so waving away "ends in 12 days" does not
 *   silence "ends in 2 days" (or a failed payment) — a higher ordinal re-arms
 *   the strip on its own.
 * - One key per subject instead of one per subject *per day*. The old shape
 *   minted a fresh `localStorage` entry every day and never swept any of them.
 *
 * Reads go through `useSyncExternalStore` so every mounted instance and every
 * tab agree; `userPrefs` is the canonical storage seam.
 */

/**
 * LOCAL calendar day. `new Date().toISOString()` rolls over at UTC midnight —
 * 17:00 for a user in Los Angeles — so "comes back tomorrow" used to mean
 * "comes back this afternoon". Built by hand rather than via
 * `toLocaleDateString` to stay free of locale/ICU surprises.
 */
export function localDay(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const snapshots = new Map<string, string | null>();
const listeners = new Map<string, Set<() => void>>();

function snapshot(key: string): string | null {
  if (!snapshots.has(key)) snapshots.set(key, getPref(key));
  return snapshots.get(key) ?? null;
}

function emit(key: string): void {
  listeners.get(key)?.forEach((fn) => fn());
}

export function dismissAtRung(key: string, ordinal: number): void {
  const value = `${localDay()}:${ordinal}`;
  // In-memory first: in private mode `setPref` is a no-op, and the strip must
  // still disappear for the rest of the session.
  snapshots.set(key, value);
  setPref(key, value);
  emit(key);
}

/** True iff dismissed TODAY at a rung at least as severe as the current one. */
export function useRungDismissed(key: string, ordinal: number): boolean {
  const raw = useSyncExternalStore(
    (onStoreChange) => {
      let set = listeners.get(key);
      if (!set) {
        set = new Set();
        listeners.set(key, set);
      }
      set.add(onStoreChange);
      const unsubscribe = subscribePref(key, (value) => {
        snapshots.set(key, value);
        emit(key);
      });
      return () => {
        set.delete(onStoreChange);
        unsubscribe();
      };
    },
    // Cached string, so the identity is stable and React does not loop.
    () => snapshot(key),
    () => null,
  );

  if (!raw) return false;
  const [day, ord] = raw.split(":");
  return day === localDay() && Number(ord) >= ordinal;
}

/** Test seam — the snapshot map is module-level and outlives a single case. */
export function __resetRungDismissals(): void {
  snapshots.clear();
  listeners.forEach((set) => set.forEach((fn) => fn()));
}

// The snapshot map is the getSnapshot source for `useRungDismissed`, and it is
// memoised for the life of the module. An account teardown removes the stored
// keys but cannot reach this map, so without it account A's "dismissed today"
// kept hiding the subscription / billing strip for account B in the same tab.
// The event name is inlined to avoid importing the teardown module here: it
// pulls in the theme + locale stores, and this file is read during first paint.
if (typeof window !== "undefined") {
  window.addEventListener("veltric:account-teardown", () => {
    __resetRungDismissals();
  });
}
